import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
  LOCALE_ID,
} from '@angular/core';
import {
  provideRouter,
  withViewTransitions,
  withDebugTracing,
} from '@angular/router';
import * as Sentry from '@sentry/angular';
import { routes } from './app.routes';
import { AppModule } from './app.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import {
  provideLottieOptions,
  provideCacheableAnimationLoader,
} from 'ngx-lottie';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  MissingTranslationHandler,
  provideTranslateService,
  TranslateLoader,
} from '@ngx-translate/core';
import { TranslationLoader } from './shared/services/translation/translation-loader';
import { CookieService } from 'ngx-cookie-service';
import { MyMissingTranslationHandler } from './shared/services/translation/my-missing-translation-handler';
import '@progress/kendo-angular-intl/locales/de/all';
import '@progress/kendo-angular-intl/locales/en/all';
import '@progress/kendo-angular-intl/locales/nl/all';

Sentry.init({
  environment: environment.environmentName,
  dsn: 'https://0390e850fc83f129d4f81f8fbf06453c@o4508206329495552.ingest.de.sentry.io/4508206413054032',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  sendClientReports: false,
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/api.dynamicmanaging\.com/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, CookieService],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MyMissingTranslationHandler,
      },
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    {
      provide: LOCALE_ID,
      useFactory: getLanguageForLocale,
      deps: [CookieService],
    },

    provideHttpClient(withInterceptorsFromDi()),
    provideLottieOptions({ player: () => import('lottie-web') }),
    provideCacheableAnimationLoader(),
    provideAnimations(),
    provideRouter(
      routes,
      withViewTransitions({ skipInitialTransition: true }),
      // withDebugTracing(),
    ),
    importProvidersFrom(AppModule),
  ],
};

export function getLanguageForLocale(cookieService: CookieService): string {
  return cookieService.get('language');
}

export function HttpLoaderFactory(
  httpClient: HttpClient,
  cookieService: CookieService,
) {
  return new TranslationLoader(
    httpClient,
    cookieService,
    'api/v2/Language/GetPreLoginTranslations?language=',
    'api/v2/Language/GetTranslationsByDictionary?dictionaryId=52B54DAD-3FA6-426F-B261-D20244634057&language=',
  );
}
